import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { useNavigate, useParams } from 'react-router-dom';
import { 
  Heart, ArrowLeft, FileText, Download, Printer, Sparkles, 
  MessageSquare, TrendingUp, Calendar, Clock, ChevronDown, ChevronUp,
  Copy, Check
} from 'lucide-react';
import { AIErrorInline } from '@/components/AIUnavailableNotice';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

export default function ReportGeneration() {
  const navigate = useNavigate();
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [reportType, setReportType] = useState('progress_summary');
  const [additionalNotes, setAdditionalNotes] = useState('');
  const [generatedReport, setGeneratedReport] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [expandedSections, setExpandedSections] = useState({
    chunks: false,
    progress: false,
    activities: false
  });
  const [copied, setCopied] = useState(false);
  const [showReportDialog, setShowReportDialog] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [letterhead, setLetterhead] = useState(null);

  useEffect(() => {
    fetchData();
  }, [childId]);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch child report data and letterhead in parallel
      const [dataRes, letterheadRes] = await Promise.all([
        axios.get(`${API}/reports/child-data/${childId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/therapist/letterhead`, {
          headers: { Authorization: `Bearer ${token}` }
        }).catch(() => ({ data: null }))
      ]);
      
      setReportData(dataRes.data);
      setChild(dataRes.data.child);
      setLetterhead(letterheadRes.data);
      
      // Fetch saved reports
      const reportsRes = await axios.get(`${API}/reports/${childId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSavedReports(reportsRes.data);
      
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateReport = async () => {
    setGeneratingReport(true);
    setAiError(null); // Clear any previous error
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/reports/generate-ai`, {
        child_id: childId,
        report_type: reportType,
        additional_notes: additionalNotes
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setGeneratedReport(response.data.report);
      setShowReportDialog(true);
      toast.success('Report generated!');
      
      // Check for usage warning
      if (response.data.usage_warning) {
        const warning = response.data.usage_warning;
        
        if (warning.level === 'limit_reached') {
          toast.error(warning.message, { duration: 8000 });
        } else if (warning.level === 'critical') {
          toast.warning(`⚠️ ${warning.message}`, { duration: 6000 });
        } else if (warning.level === 'warning') {
          toast.info(`📊 ${warning.message}`, { duration: 5000 });
        }
      }
      
      fetchData(); // Refresh saved reports
    } catch (error) {
      console.error('AI report generation error:', error);
      const statusCode = error.response?.status;
      const errorDetail = error.response?.data?.detail || error.message || 'Failed to generate report';
      
      // Check if it's an AI service unavailability issue
      if (statusCode === 500 || statusCode === 503 || statusCode === 429 || 
          errorDetail.toLowerCase().includes('token') || 
          errorDetail.toLowerCase().includes('rate') ||
          errorDetail.toLowerCase().includes('unavailable') ||
          errorDetail.toLowerCase().includes('api')) {
        setAiError('AI report generation is temporarily unavailable. Please try again in a few minutes.');
      } else {
        toast.error(errorDetail);
      }
    } finally {
      setGeneratingReport(false);
    }
  };

  const handleExportJSON = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/reports/export/${childId}?format=json`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const blob = new Blob([JSON.stringify(response.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${child?.name?.replace(/\s+/g, '_')}_data_export.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Data exported!');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleExportCSV = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/reports/export/${childId}?format=csv`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Convert chunks to CSV
      const chunks = response.data.chunks_csv;
      if (chunks.length > 0) {
        const headers = ['Date', 'Text', 'Function', 'Context', 'Type'];
        const rows = chunks.map(c => [
          c.created_at || '',
          `"${(c.text || '').replace(/"/g, '""')}"`,
          c.function || '',
          `"${(c.context || '').replace(/"/g, '""')}"`,
          c.type || ''
        ]);
        const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
        
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${child?.name?.replace(/\s+/g, '_')}_chunks.csv`;
        a.click();
        URL.revokeObjectURL(url);
      }
      
      toast.success('Data exported as CSV!');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handlePrintReport = () => {
    if (!generatedReport) return;
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast.error('Please allow popups to print');
      return;
    }
    
    // Build letterhead HTML
    let letterheadHTML = '';
    if (letterhead?.has_letterhead || letterhead?.practice_name) {
      letterheadHTML = '<div style="text-align: center; margin-bottom: 24pt; border-bottom: 2px solid #333; padding-bottom: 16pt;">';
      if (letterhead?.letterhead_image) {
        letterheadHTML += `<img src="${letterhead.letterhead_image}" alt="Letterhead" style="max-height: 80px; margin-bottom: 8pt;" />`;
      }
      if (letterhead?.practice_name) {
        letterheadHTML += `<h2 style="margin: 0; font-size: 14pt;">${letterhead.practice_name}</h2>`;
      }
      if (letterhead?.practice_address) {
        letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${letterhead.practice_address.replace(/\n/g, '<br>')}</p>`;
      }
      const contactInfo = [letterhead?.practice_phone, letterhead?.practice_email].filter(Boolean).join(' | ');
      if (contactInfo) {
        letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${contactInfo}</p>`;
      }
      if (letterhead?.registration_number) {
        letterheadHTML += `<p style="margin: 4pt 0; font-size: 9pt; color: #777;">${letterhead.registration_number}</p>`;
      }
      letterheadHTML += '</div>';
    }
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Report - ${child?.name}</title>
          <style>
            body { font-family: 'Segoe UI', sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
            h1 { color: #4a6355; border-bottom: 2px solid #e5e5e5; padding-bottom: 10px; }
            .meta { color: #666; font-size: 0.9rem; margin-bottom: 20px; }
            .content { white-space: pre-wrap; }
          </style>
        </head>
        <body>
          ${letterheadHTML}
          <h1>${reportType === 'clinical_report' ? 'Clinical Report' : reportType === 'parent_summary' ? 'Parent Summary' : 'Progress Summary'} - ${child?.name}</h1>
          <div class="meta">Generated: ${new Date().toLocaleDateString()}</div>
          <div class="content">${generatedReport}</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const copyToClipboard = async () => {
    if (!generatedReport) return;
    
    try {
      // Build letterhead text for plain text version
      let letterheadText = '';
      if (letterhead?.has_letterhead || letterhead?.practice_name) {
        if (letterhead?.practice_name) {
          letterheadText += letterhead.practice_name + '\n';
        }
        if (letterhead?.practice_address) {
          letterheadText += letterhead.practice_address + '\n';
        }
        const contactInfo = [letterhead?.practice_phone, letterhead?.practice_email].filter(Boolean).join(' | ');
        if (contactInfo) {
          letterheadText += contactInfo + '\n';
        }
        if (letterhead?.registration_number) {
          letterheadText += letterhead.registration_number + '\n';
        }
        letterheadText += '\n' + '─'.repeat(50) + '\n\n';
      }
      
      const plainTextReport = letterheadText + generatedReport;
      
      // Build HTML version with letterhead for Word compatibility
      let letterheadHTML = '';
      if (letterhead?.has_letterhead || letterhead?.practice_name) {
        letterheadHTML = '<div style="text-align: center; margin-bottom: 24pt; border-bottom: 2px solid #333; padding-bottom: 16pt;">';
        if (letterhead?.letterhead_image) {
          letterheadHTML += `<img src="${letterhead.letterhead_image}" alt="Letterhead" style="max-height: 80px; margin-bottom: 8pt;" />`;
        }
        if (letterhead?.practice_name) {
          letterheadHTML += `<h2 style="margin: 0; font-size: 14pt;">${letterhead.practice_name}</h2>`;
        }
        if (letterhead?.practice_address) {
          letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${letterhead.practice_address.replace(/\n/g, '<br>')}</p>`;
        }
        const contactInfo = [letterhead?.practice_phone, letterhead?.practice_email].filter(Boolean).join(' | ');
        if (contactInfo) {
          letterheadHTML += `<p style="margin: 4pt 0; font-size: 10pt; color: #555;">${contactInfo}</p>`;
        }
        if (letterhead?.registration_number) {
          letterheadHTML += `<p style="margin: 4pt 0; font-size: 9pt; color: #777;">${letterhead.registration_number}</p>`;
        }
        letterheadHTML += '</div>';
      }
      
      const htmlReport = `
        <html>
        <body style="font-family: Arial, sans-serif; font-size: 11pt; line-height: 1.6;">
          ${letterheadHTML}
          <div style="white-space: pre-wrap;">${generatedReport.replace(/\n/g, '<br>')}</div>
        </body>
        </html>
      `;
      
      // Try to copy with rich text formatting for Word compatibility
      if (navigator.clipboard.write) {
        try {
          const htmlBlob = new Blob([htmlReport], { type: 'text/html' });
          const textBlob = new Blob([plainTextReport], { type: 'text/plain' });
          
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/html': htmlBlob,
              'text/plain': textBlob
            })
          ]);
          toast.success('Report copied with letterhead! Paste into Word for best formatting.');
        } catch (htmlErr) {
          // Fallback to plain text
          await navigator.clipboard.writeText(plainTextReport);
          toast.success('Report copied to clipboard!');
        }
      } else {
        await navigator.clipboard.writeText(plainTextReport);
        toast.success('Report copied to clipboard!');
      }
      
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy to clipboard');
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getCommunicationStyleDisplay = () => {
    if (!child) return '';
    let display = child.communication_style;
    if (child.communication_style === 'GLP' && child.glp_stage) {
      display += ` (Stage ${child.glp_stage})`;
    } else if (child.communication_style === 'Analytic' && child.analytic_level) {
      display += ` (${child.analytic_level})`;
    }
    if (child.uses_aac) {
      display += ` + AAC`;
    }
    return display;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <p className="text-stone-600">Loading report data...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate(`/children/${childId}`)}
              className="rounded-full"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <Heart className="w-5 h-5 text-primary" />
              </div>
              <h1 className="text-2xl font-serif text-primary-900">PlayFirst</h1>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-8">
            <h2 className="text-3xl font-serif text-primary-900 mb-2">
              Reports & Data
            </h2>
            <p className="text-lg text-stone-600">{child?.name}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Left Column - Data Overview */}
            <div className="lg:col-span-2 space-y-6">
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-4 bg-white rounded-2xl text-center">
                  <MessageSquare className="w-8 h-8 mx-auto mb-2 text-primary" />
                  <p className="text-2xl font-bold text-primary-900">{reportData?.summary?.total_chunks || 0}</p>
                  <p className="text-xs text-stone-500">Language Chunks</p>
                </Card>
                <Card className="p-4 bg-white rounded-2xl text-center">
                  <TrendingUp className="w-8 h-8 mx-auto mb-2 text-green-600" />
                  <p className="text-2xl font-bold text-primary-900">{reportData?.summary?.total_progress_notes || 0}</p>
                  <p className="text-xs text-stone-500">Progress Notes</p>
                </Card>
                <Card className="p-4 bg-white rounded-2xl text-center">
                  <Calendar className="w-8 h-8 mx-auto mb-2 text-blue-600" />
                  <p className="text-2xl font-bold text-primary-900">{reportData?.summary?.total_weekly_focuses || 0}</p>
                  <p className="text-xs text-stone-500">Weekly Focuses</p>
                </Card>
                <Card className="p-4 bg-white rounded-2xl text-center">
                  <Check className="w-8 h-8 mx-auto mb-2 text-purple-600" />
                  <p className="text-2xl font-bold text-primary-900">{reportData?.summary?.activities_completed || 0}</p>
                  <p className="text-xs text-stone-500">Activities Done</p>
                </Card>
              </div>

              {/* Child Profile Summary */}
              <Card className="p-6 bg-white rounded-3xl" data-testid="profile-summary">
                <h3 className="text-lg font-semibold text-primary-900 mb-4">Profile Summary</h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-stone-500">Age</p>
                    <p className="font-medium">{child?.age} years</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Communication Style</p>
                    <p className="font-medium">{getCommunicationStyleDisplay()}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Interests</p>
                    <p className="font-medium">{child?.interests?.join(', ')}</p>
                  </div>
                  <div>
                    <p className="text-stone-500">Strengths</p>
                    <p className="font-medium">{child?.strengths_notes}</p>
                  </div>
                  {child?.uses_aac && (
                    <>
                      <div>
                        <p className="text-stone-500">AAC Access</p>
                        <p className="font-medium capitalize">{child?.aac_access_method?.replace('_', ' ') || 'Not specified'}</p>
                      </div>
                      {child?.core_words_focus?.length > 0 && (
                        <div>
                          <p className="text-stone-500">Core Words Focus</p>
                          <p className="font-medium">{child.core_words_focus.join(', ')}</p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              </Card>

              {/* Expandable Data Sections */}
              <Card className="bg-white rounded-3xl overflow-hidden">
                {/* Chunks Section */}
                <div className="border-b border-stone-100">
                  <button
                    onClick={() => toggleSection('chunks')}
                    className="w-full p-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
                    data-testid="toggle-chunks"
                  >
                    <div className="flex items-center gap-3">
                      <MessageSquare className="w-5 h-5 text-primary" />
                      <span className="font-medium">Language Chunks ({reportData?.chunks?.length || 0})</span>
                      <span className="text-xs text-stone-500">
                        {reportData?.summary?.spontaneous_chunks} spontaneous, {reportData?.summary?.echoed_chunks} echoed
                      </span>
                    </div>
                    {expandedSections.chunks ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.chunks && (
                    <div className="p-4 pt-0 max-h-64 overflow-y-auto">
                      {reportData?.chunks?.length > 0 ? (
                        <div className="space-y-2">
                          {reportData.chunks.map((chunk, i) => (
                            <div key={i} className="p-3 bg-stone-50 rounded-xl text-sm">
                              <p className="font-medium">&quot;{chunk.text}&quot;</p>
                              <p className="text-stone-500 text-xs mt-1">
                                {chunk.function} • {chunk.type} • {chunk.context}
                              </p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-stone-500 text-sm">No chunks recorded yet</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Progress Section */}
                <div className="border-b border-stone-100">
                  <button
                    onClick={() => toggleSection('progress')}
                    className="w-full p-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
                    data-testid="toggle-progress"
                  >
                    <div className="flex items-center gap-3">
                      <TrendingUp className="w-5 h-5 text-green-600" />
                      <span className="font-medium">Progress Notes ({reportData?.progress?.length || 0})</span>
                    </div>
                    {expandedSections.progress ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.progress && (
                    <div className="p-4 pt-0 max-h-64 overflow-y-auto">
                      {reportData?.progress?.length > 0 ? (
                        <div className="space-y-2">
                          {reportData.progress.map((p, i) => (
                            <div key={i} className="p-3 bg-stone-50 rounded-xl text-sm">
                              <p className="text-xs text-stone-500 mb-1">{p.date} • {p.category}</p>
                              <p>{p.observation}</p>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-stone-500 text-sm">No progress notes yet</p>
                      )}
                    </div>
                  )}
                </div>

                {/* Activities Section */}
                <div>
                  <button
                    onClick={() => toggleSection('activities')}
                    className="w-full p-4 flex items-center justify-between hover:bg-stone-50 transition-colors"
                    data-testid="toggle-activities"
                  >
                    <div className="flex items-center gap-3">
                      <Check className="w-5 h-5 text-purple-600" />
                      <span className="font-medium">Activity Completions ({reportData?.activity_completions?.length || 0})</span>
                    </div>
                    {expandedSections.activities ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                  </button>
                  {expandedSections.activities && (
                    <div className="p-4 pt-0 max-h-64 overflow-y-auto">
                      {reportData?.activity_completions?.length > 0 ? (
                        <div className="space-y-2">
                          {reportData.activity_completions.map((c, i) => (
                            <div key={i} className="p-3 bg-stone-50 rounded-xl text-sm flex items-start gap-3">
                              <div className={`w-5 h-5 rounded-full flex items-center justify-center ${c.completed ? 'bg-green-100 text-green-600' : 'bg-stone-200 text-stone-400'}`}>
                                {c.completed ? '✓' : '○'}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium">{c.completed ? 'Completed' : 'Not completed'}</p>
                                {c.parent_comment && (
                                  <p className="text-stone-600 text-xs mt-1">&quot;{c.parent_comment}&quot;</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-stone-500 text-sm">No activity completions yet</p>
                      )}
                    </div>
                  )}
                </div>
              </Card>
            </div>

            {/* Right Column - Actions */}
            <div className="space-y-6">
              {/* Generate Report */}
              <Card className="p-6 bg-white rounded-3xl" data-testid="generate-report-card">
                <div className="flex items-center gap-3 mb-4">
                  <Sparkles className="w-6 h-6 text-purple-600" />
                  <h3 className="text-lg font-semibold text-primary-900">AI Report</h3>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label className="text-sm">Report Type</Label>
                    <Select value={reportType} onValueChange={setReportType}>
                      <SelectTrigger className="rounded-xl mt-1" data-testid="report-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="progress_summary">Progress Summary</SelectItem>
                        <SelectItem value="clinical_report">Clinical Report</SelectItem>
                        <SelectItem value="parent_summary">Parent Summary</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label className="text-sm">Additional Notes (optional)</Label>
                    <Textarea
                      value={additionalNotes}
                      onChange={(e) => setAdditionalNotes(e.target.value)}
                      placeholder="Any specific areas to focus on..."
                      className="rounded-xl mt-1 min-h-20"
                      data-testid="additional-notes"
                    />
                  </div>
                  
                  {aiError && (
                    <AIErrorInline 
                      message={aiError} 
                      onRetry={() => {
                        setAiError(null);
                        handleGenerateReport();
                      }} 
                    />
                  )}
                  
                  <Button
                    onClick={handleGenerateReport}
                    disabled={generatingReport}
                    className="w-full rounded-full"
                    data-testid="generate-report-btn"
                  >
                    {generatingReport ? (
                      <>Generating...</>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4 mr-2" />
                        Generate Report
                      </>
                    )}
                  </Button>
                </div>
              </Card>

              {/* Export Options */}
              <Card className="p-6 bg-white rounded-3xl" data-testid="export-card">
                <div className="flex items-center gap-3 mb-4">
                  <Download className="w-6 h-6 text-blue-600" />
                  <h3 className="text-lg font-semibold text-primary-900">Export Data</h3>
                </div>
                
                <div className="space-y-3">
                  <Button
                    variant="outline"
                    onClick={handleExportJSON}
                    className="w-full rounded-full"
                    data-testid="export-json-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export as JSON
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleExportCSV}
                    className="w-full rounded-full"
                    data-testid="export-csv-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Export Chunks as CSV
                  </Button>
                </div>
              </Card>

              {/* Saved Reports */}
              {savedReports.length > 0 && (
                <Card className="p-6 bg-white rounded-3xl" data-testid="saved-reports">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="w-6 h-6 text-stone-600" />
                    <h3 className="text-lg font-semibold text-primary-900">Recent Reports</h3>
                  </div>
                  
                  <div className="space-y-2">
                    {savedReports.slice(0, 5).map((report, i) => (
                      <button
                        key={i}
                        onClick={() => {
                          setGeneratedReport(report.content);
                          setShowReportDialog(true);
                        }}
                        className="w-full p-3 bg-stone-50 rounded-xl text-left hover:bg-stone-100 transition-colors"
                      >
                        <p className="text-sm font-medium capitalize">
                          {report.report_type?.replace('_', ' ')}
                        </p>
                        <p className="text-xs text-stone-500">
                          {new Date(report.created_at).toLocaleDateString()}
                        </p>
                      </button>
                    ))}
                  </div>
                </Card>
              )}
            </div>
          </div>
        </motion.div>
      </main>

      {/* Report Dialog */}
      <Dialog open={showReportDialog} onOpenChange={setShowReportDialog}>
        <DialogContent className="rounded-3xl max-w-3xl max-h-[80vh] overflow-hidden" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl font-serif flex items-center justify-between">
              <span>Generated Report</span>
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={copyToClipboard} className="rounded-full">
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button variant="ghost" size="sm" onClick={handlePrintReport} className="rounded-full">
                  <Printer className="w-4 h-4" />
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto max-h-[60vh] pr-2">
            <div className="prose prose-sm max-w-none whitespace-pre-wrap text-stone-700">
              {generatedReport}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
